/**
 * useDebouncedValue — returns a debounced copy of the input value.
 *
 * The returned value only updates after `ms` milliseconds have elapsed without
 * a change to the source value. Useful for filter inputs where the filter
 * work is expensive but typing should remain responsive.
 *
 * @example
 *   const [search, setSearch] = useState('')
 *   const debouncedSearch = useDebouncedValue(search, 150)
 *   const results = useMemo(() => filter(list, debouncedSearch), [list, debouncedSearch])
 */

import { useState, useEffect } from 'react'

export default function useDebouncedValue<T> (value: T, ms: number): T {
  const [debounced, setDebounced] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value)
    }, ms)
    return () => clearTimeout(timer)
  }, [value, ms])

  return debounced
}
